import type {TranslationKeys} from "@agenta/i18n"
import {atom} from "jotai"

/** Set by `useAuthSuccess`, cleared by `PostAuthLoader` once the first screen has its data. */
/** `pending`: an OIDC code is still being exchanged, so the account kind is not known yet. */
export type AccountKind = "new" | "returning" | "pending"

export interface PostAuthBoot {
    account: AccountKind
    /** Answers older than this predate the sign-in and say nothing about readiness. */
    startedAt: number
}

export const postAuthBootAtom = atom<PostAuthBoot | null>(null)

/** What the projects query has said since the sign-in. */
export type ProjectsAnswer = "pending" | "ok" | "empty" | "failed"

/** 0: resolving the workspace, 1: loading the project's agents, 2: everything has answered. */
export type BootStage = 0 | 1 | 2

/** Only a project with agents to load has a stage 1; any other answer is final. */
export const bootStage = (projects: ProjectsAnswer, agentsSettled: boolean): BootStage =>
    projects === "pending" ? 0 : projects === "ok" && !agentsSettled ? 1 : 2

/** Catalog keys, translated where the loader renders them. */
export const BOOT_STATUSES: Record<
    AccountKind,
    readonly [TranslationKeys, TranslationKeys, TranslationKeys]
> = {
    new: [
        "com_nav_boot_creating_workspace",
        "com_nav_boot_first_project",
        "com_nav_boot_almost_ready",
    ],
    returning: [
        "com_nav_boot_loading_agents",
        "com_nav_boot_restoring_sessions",
        "com_nav_boot_almost_ready",
    ],
    pending: [
        "com_nav_boot_signing_in",
        "com_nav_boot_loading_workspace",
        "com_nav_boot_almost_ready",
    ],
}

/** Catalog keys, translated where the loader renders them. */
export const BOOT_TIPS: readonly TranslationKeys[] = [
    "com_nav_boot_tip_channels",
    "com_nav_boot_tip_memory",
    "com_nav_boot_tip_sandbox",
    "com_nav_boot_tip_triggers",
    "com_nav_boot_tip_subscription",
]

/** Shortest hold, so a fast boot does not flash. */
export const BOOT_MIN_MS = 1200
/** Longest hold: past it the loader steps aside and the screen shows its own state. */
export const BOOT_MAX_MS = 20_000

const STAGE_RANGE: Record<BootStage, [number, number]> = {0: [4, 38], 1: [40, 78], 2: [100, 100]}

/** Bar fill in percent: a stage's floor, creeping toward its ceiling while it waits. */
export const bootProgress = (stage: BootStage, msInStage: number): number => {
    const [floor, ceiling] = STAGE_RANGE[stage]
    return Math.round(floor + (ceiling - floor) * (1 - Math.exp(-msInStage / 1500)))
}
